package br.com.vvrefeicoes.portal.feature.rmp

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.RadioButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import br.com.vvrefeicoes.portal.core.ui.components.AppBanner
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.file.PickedFile
import br.com.vvrefeicoes.portal.core.util.file.isPdfUploadSupported
import br.com.vvrefeicoes.portal.core.util.file.pickPdfFile
import br.com.vvrefeicoes.portal.domain.model.RmpApproval
import br.com.vvrefeicoes.portal.domain.model.RmpTransferChecklist
import com.composables.icons.lucide.Download
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.Save
import com.composables.icons.lucide.Trash2
import com.composables.icons.lucide.Upload
import com.composables.icons.lucide.X
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.rmp_checklist_aso
import portalvv.shared.generated.resources.rmp_checklist_observation
import portalvv.shared.generated.resources.rmp_checklist_save
import portalvv.shared.generated.resources.rmp_checklist_signature
import portalvv.shared.generated.resources.rmp_checklist_title
import portalvv.shared.generated.resources.rmp_decision_cancel
import portalvv.shared.generated.resources.rmp_new_attach
import portalvv.shared.generated.resources.rmp_new_attach_pending
import portalvv.shared.generated.resources.rmp_new_attach_unsupported

/** Campos de anexo do checklist (mesmos nomes da API). */
internal const val RMP_CHECKLIST_ASO = "aso"
internal const val RMP_CHECKLIST_SIGNATURE = "assinatura_funcionario"

private val CHECKLIST_VALUES = listOf("OK", "N/A")

/**
 * Checklist da transferência (como o modal do sistema antigo): cada item OK ou N/A, observação e os PDFs do ASO e da
 * assinatura do funcionário. Sem ele completo nenhuma etapa da transferência é aprovada.
 */
@Composable
internal fun RmpTransferChecklistDialog(
    approval: RmpApproval,
    checklist: RmpTransferChecklist,
    controller: RmpApprovalController,
    onDismiss: () -> Unit,
    onSaved: () -> Unit,
) {
    var values by remember(checklist) {
        mutableStateOf(checklist.items.mapNotNull { item -> item.value?.let { item.key to it } }.toMap())
    }
    var observation by remember(checklist) { mutableStateOf(checklist.observation.orEmpty()) }
    var pending by remember { mutableStateOf<Map<String, List<PickedFile>>>(emptyMap()) }
    val scope = rememberCoroutineScope()

    AppModalCard(
        title =
            stringResource(
                Res.string.rmp_checklist_title,
                approval.request.number
                    ?.toString()
                    .orEmpty(),
            ),
        onDismiss = onDismiss,
    ) {
        controller.errorMessage?.let { message ->
            AppBanner(text = message, onDismiss = controller::dismissError, isError = true)
        }
        checklist.items.forEach { item ->
            Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Text(text = item.label, style = MaterialTheme.typography.bodyMedium, modifier = Modifier.weight(1f))
                CHECKLIST_VALUES.forEach { option ->
                    Row(
                        modifier = Modifier.clickable { values = values + (item.key to option) },
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        RadioButton(selected = values[item.key] == option, onClick = { values = values + (item.key to option) })
                        Text(text = option, style = MaterialTheme.typography.bodyMedium)
                    }
                }
            }
        }
        AppTextField(
            value = observation,
            onValueChange = { observation = it.take(RMP_APPROVAL_NOTE_MAX) },
            label = stringResource(Res.string.rmp_checklist_observation),
            singleLine = false,
            modifier = Modifier.fillMaxWidth(),
        )
        listOf(
            RMP_CHECKLIST_ASO to stringResource(Res.string.rmp_checklist_aso),
            RMP_CHECKLIST_SIGNATURE to stringResource(Res.string.rmp_checklist_signature),
        ).forEach { (field, label) ->
            HorizontalDivider()
            Text(text = label, style = MaterialTheme.typography.titleSmall)
            approval.request.attachments.filter { it.field == field }.forEach { attachment ->
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
                    Text(attachment.fileName, style = MaterialTheme.typography.bodyMedium, modifier = Modifier.weight(1f))
                    IconButton(onClick = { controller.downloadAttachment(attachment) }) {
                        Icon(Lucide.Download, contentDescription = null)
                    }
                    IconButton(onClick = { controller.removeAttachment(attachment) }, enabled = !controller.deciding) {
                        Icon(Lucide.Trash2, contentDescription = null)
                    }
                }
            }
            val pendingLabel = stringResource(Res.string.rmp_new_attach_pending)
            pending[field].orEmpty().forEachIndexed { index, file ->
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
                    Text("${file.name} ($pendingLabel)", style = MaterialTheme.typography.bodyMedium, modifier = Modifier.weight(1f))
                    IconButton(onClick = {
                        val files = pending[field].orEmpty().filterIndexed { position, _ -> position != index }
                        pending = if (files.isEmpty()) pending - field else pending + (field to files)
                    }) {
                        Icon(Lucide.X, contentDescription = null)
                    }
                }
            }
            if (isPdfUploadSupported()) {
                AppButton(
                    text = stringResource(Res.string.rmp_new_attach),
                    onClick = {
                        scope.launch {
                            val file = pickPdfFile() ?: return@launch
                            if (file.name.endsWith(".pdf", ignoreCase = true)) {
                                pending = pending + (field to pending[field].orEmpty() + file)
                            }
                        }
                    },
                    enabled = !controller.deciding,
                    variant = PortalButtonVariant.Outlined,
                    leadingIcon = Lucide.Upload,
                )
            } else {
                Text(
                    stringResource(Res.string.rmp_new_attach_unsupported),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
        HorizontalDivider()
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12, Alignment.End),
        ) {
            AppButton(
                text = stringResource(Res.string.rmp_decision_cancel),
                onClick = onDismiss,
                enabled = !controller.deciding,
                variant = PortalButtonVariant.Text,
            )
            AppButton(
                text = stringResource(Res.string.rmp_checklist_save),
                onClick = { controller.saveChecklist(values, observation, pending, onSaved) },
                enabled = !controller.deciding,
                variant = PortalButtonVariant.Filled,
                leadingIcon = Lucide.Save,
            )
        }
    }
}
